import React, { useState, useMemo, useCallback } from 'react';
import CustomStackedBarChart from 'shared/charts/StackedBarChart';
import CustomPieChart from 'shared/charts/PieChart';
import { useLayout } from 'shared/contexts/LayoutContext';

// ─── Formatters ───────────────────────────────────────────────────────────────

const formatCompact = (val) => {
  if (!val) return '0';
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return (val / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (abs >= 1_000)     return (val / 1_000).toFixed(1).replace(/\.0$/, '') + 'k';
  return Math.round(val).toLocaleString('ru-RU');
};

const formatNum = (val) => (val ? val.toLocaleString('ru-RU') : '0');

const FORMATTERS = {
  sales_count: formatNum,
  count:       formatNum,
  avg_cheque:  formatCompact,
  sum:         formatCompact,
  profit:      formatCompact,
};

// ─── Constants ────────────────────────────────────────────────────────────────

/** All client segment definitions. Order controls table column order. */
const CLIENT_GROUPS = [
  {
    key: 'all_clients',
    label: 'Все',
    labelFull: 'Все клиенты',
    color: {
      header: 'bg-blue-50 text-blue-700',
      cell:   'bg-blue-50/30 text-blue-700',
      foot:   'bg-blue-100/50 text-blue-800',
      badge:  'bg-blue-100',
      border: 'border-blue-300',
    },
    definition: 'Все уникальные клиенты, совершившие покупки у консультанта за период.',
  },
  {
    key: 'retail_clients',
    label: 'Розница',
    labelFull: 'Розничные клиенты',
    color: {
      header: 'bg-gray-50 text-gray-600',
      cell:   'bg-gray-50/30 text-gray-600',
      foot:   'bg-gray-100/50 text-gray-700',
      badge:  'bg-gray-200',
      border: 'border-gray-300',
    },
    definition: 'Анонимные покупатели — не сохранены в базе, проходят как единый «розничный» клиент.',
  },
  {
    key: 'added_clients',
    label: 'Добавленные',
    labelFull: 'Добавленные клиенты',
    color: {
      header: 'bg-violet-50 text-violet-700',
      cell:   'bg-violet-50/30 text-violet-700',
      foot:   'bg-violet-100/50 text-violet-800',
      badge:  'bg-violet-100',
      border: 'border-violet-300',
    },
    definition: 'Клиенты, сохранённые в базе Cloudshop — необязательно используют приложение или являются новыми.',
  },
  {
    key: 'app_clients',
    label: 'App',
    labelFull: 'Пользователи App',
    color: {
      header: 'bg-orange-50 text-orange-700',
      cell:   'bg-orange-50/30 text-orange-700',
      foot:   'bg-orange-100/50 text-orange-800',
      badge:  'bg-orange-100',
      border: 'border-orange-300',
    },
    definition: 'Клиенты, авторизованные через мобильное приложение.',
  },
  {
    key: 'new_clients',
    label: 'Новые',
    labelFull: 'Новые клиенты',
    color: {
      header: 'bg-green-50 text-green-700',
      cell:   'bg-green-50/30 text-green-700',
      foot:   'bg-green-100/50 text-green-800',
      badge:  'bg-green-100',
      border: 'border-green-300',
    },
    definition: 'Клиенты, совершившие первую покупку в данном периоде.',
  },
];

/** Metric options shown in the shared tab picker. */
const METRIC_OPTIONS = [
  { key: 'sales_count', label: 'Кол-во продаж' },
  { key: 'count',       label: 'Уник. клиенты'  },
  { key: 'avg_cheque',  label: 'Ср. чек'        },
  { key: 'sum',         label: 'Выручка'         },
  { key: 'profit',      label: 'Прибыль'         },
];

/**
 * On the "unique clients" tab, retail_clients is always 0/1 and added_clients
 * fully overlaps all_clients — hide both to avoid misleading numbers.
 */
const COUNT_HIDDEN_GROUPS = new Set(['retail_clients', 'added_clients']);

/** Bar-chart fill colours keyed by client group key. */
const BAR_COLORS = {
  all_clients:    '#818cf8',
  retail_clients: '#94a3b8',
  added_clients:  '#a78bfa',
  app_clients:    '#fb923c',
  new_clients:    '#4ade80',
};

// ─── Pure data helpers ────────────────────────────────────────────────────────

/** Aggregate grand totals for every client group across all consultants. */
function buildGrandTotals(rows) {
  const totals = Object.fromEntries(
    CLIENT_GROUPS.map(({ key }) => [
      key,
      { sum: 0, profit: 0, sales_count: 0, count: 0, avg_cheque: 0 },
    ])
  );

  rows.forEach((item) => {
    CLIENT_GROUPS.forEach(({ key }) => {
      const src = item[key] || {};
      totals[key].sum         += src.sum         || 0;
      totals[key].profit      += src.profit      || 0;
      totals[key].sales_count += src.sales_count || 0;
      totals[key].count       += src.count       || 0;
    });
  });

  // Weighted average cheque = total revenue ÷ total sales
  CLIENT_GROUPS.forEach(({ key }) => {
    const g = totals[key];
    g.avg_cheque = g.sales_count > 0 ? g.sum / g.sales_count : 0;
  });

  return totals;
}

/** Build data array consumed by the stacked bar chart. */
function buildChartData(rows, metric) {
  const hideSubGroups = metric === 'count';
  return rows.map((item) => {
    const consultantMeta = item.consultant?.meta || item.consultant || {};
    const entry = { name: consultantMeta.name };
    CLIENT_GROUPS.forEach(({ key, labelFull }) => {
      if (hideSubGroups && COUNT_HIDDEN_GROUPS.has(key)) return;
      entry[labelFull] = (item[key] || {})[metric] || 0;
    });
    return entry;
  });
}

// ─── Sub-components ───────────────────────────────────────────────────────────

/** Coloured summary card shown above the table. */
function SummaryCard({ group, metric, value, allValue }) {
  const fmt     = FORMATTERS[metric] || formatNum;
  const showPct = group.key !== 'all_clients' && allValue > 0;
  const pct     = showPct ? ((value / allValue) * 100).toFixed(1) : null;

  return (
    <div className={`bg-white p-5 rounded-xl shadow-sm border-l-4 ${group.color.border}`}>
      <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold">{group.labelFull}</p>
      <p className="text-xl font-bold text-gray-800 mt-1">{fmt(value)}</p>
      {pct && <p className="text-xs text-gray-400 mt-1">{pct}% от всех</p>}
    </div>
  );
}

/** Collapsible section explaining each client group. */
function DefinitionsPanel({ open, onToggle }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-6 py-3 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <span className="text-base">ℹ️</span>
          Определения статусов клиентов
        </span>
        <span className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}>▾</span>
      </button>

      {open && (
        <div className="px-6 pb-5 pt-1 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 border-t border-gray-100">
          {CLIENT_GROUPS.map((group) => (
            <div key={group.key} className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50">
              <span className={`mt-1 inline-block w-3 h-3 rounded-full flex-shrink-0 ${group.color.badge}`} />
              <div>
                <p className={`text-sm font-bold mb-1 ${group.color.cell.split(' ').find((c) => c.startsWith('text-'))}`}>
                  {group.labelFull}
                </p>
                <p className="text-sm text-gray-500 leading-relaxed">{group.definition}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Secondary cell content: % of all, or ± delta for avg_cheque. */
function SecondaryCell({ metric, value, allValue, colorClass, bold = false }) {
  const fmt   = FORMATTERS[metric] || formatNum;
  const fontW = bold ? 'font-bold' : 'font-medium';

  if (metric === 'avg_cheque') {
    const delta        = value - allValue;
    const sign         = delta >= 0 ? '+' : '−';
    const absFormatted = fmt(Math.abs(delta));
    const colorBadge   =
      delta === 0 ? colorClass
      : delta > 0 ? 'bg-green-100 text-green-700'
      :             'bg-red-100 text-red-600';

    return (
      <span className={`px-2 py-0.5 rounded-full text-xs ${fontW} ${colorBadge}`}>
        {sign}{absFormatted}
      </span>
    );
  }

  return (
    <span className={`px-2 py-0.5 rounded-full ${colorClass} text-xs ${fontW}`}>
      {allValue > 0 ? ((value / allValue) * 100).toFixed(1) : '0.0'}%
    </span>
  );
}

/** Metric tab-bar + sort toggle rendered inside the table header area. */
function MetricTabBar({ metric, onMetricChange, sortEnabled, onSortToggle }) {
  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
      {/* Metric tabs */}
      <div className="flex bg-gray-100 p-1 rounded-lg flex-wrap gap-1">
        {METRIC_OPTIONS.map((opt) => (
          <button
            key={opt.key}
            onClick={() => onMetricChange(opt.key)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              metric === opt.key
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Sort toggle */}
      <button
        onClick={onSortToggle}
        title={
          sortEnabled
            ? 'Сортировка по метрике активна — нажмите для сброса'
            : 'Сортировать по выбранной метрике (убывание)'
        }
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
          sortEnabled
            ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
            : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
        }`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3 6h18M7 12h10M11 18h2" />
        </svg>
        {sortEnabled ? 'По метрике ↓' : 'По ID'}
      </button>
    </div>
  );
}

/** Modal dialog displaying detailed statistics for a selected consultant. */
function ConsultantSegmentStatsModal({ consultantData }) {
  if (!consultantData) return null;
  const { openModal } = useLayout();

  const { consultant, all_clients = {} } = consultantData;
  const consultantMeta = consultant?.meta || consultant || {};
  const metaObj        = consultant?.meta || (consultantMeta.type ? consultantMeta : null);
  const totalSales  = all_clients.sales_count || 0;
  const totalCount  = all_clients.count || 0;
  const totalSum    = all_clients.sum || 0;
  const totalProfit = all_clients.profit || 0;
  const totalAvg    = all_clients.avg_cheque || 0;

  const marginPct = consultantData.margin_pct != null
    ? Number(consultantData.margin_pct).toFixed(1)
    : totalSum > 0 ? ((totalProfit / totalSum) * 100).toFixed(1) : '0.0';
  const appPct    = consultantData.app_pct != null
    ? Number(consultantData.app_pct).toFixed(1)
    : totalCount > 0 ? (((consultantData.app_clients?.count || 0) / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="space-y-6 text-gray-800 p-1">
      {/* ── Subtitle / ID Info Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-blue-50/70 p-4 rounded-xl border border-blue-100">
        <div>
          {metaObj ? (
            <button
              type="button"
              onClick={() => openModal({ meta: metaObj })}
              className="group inline-flex items-center gap-1.5 text-left text-base font-bold text-blue-900 hover:text-blue-700 hover:underline cursor-pointer focus:outline-none"
              title="Открыть карточку консультанта"
            >
              <span>{consultantMeta.name}</span>
              <svg className="w-4 h-4 text-blue-600 group-hover:text-blue-700 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          ) : (
            <h4 className="text-base font-bold text-blue-900">{consultantMeta.name}</h4>
          )}
          <p className="text-xs text-blue-600 mt-0.5">ID консультанта: #{consultantMeta.id}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            Рентабельность: {marginPct}%
          </span>
          <span className="px-3 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-800">
            App пользователи: {appPct}%
          </span>
        </div>
      </div>

      {/* ── Key Indicators Grid ── */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Выручка</p>
          <p className="text-lg font-bold text-gray-900 mt-1">{formatCompact(totalSum)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalSum)} сом</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Прибыль</p>
          <p className="text-lg font-bold text-green-700 mt-1">{formatCompact(totalProfit)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(totalProfit)} сом</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Продажи</p>
          <p className="text-lg font-bold text-indigo-700 mt-1">{formatNum(totalSales)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">чеков</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Уник. клиенты</p>
          <p className="text-lg font-bold text-violet-700 mt-1">{formatNum(totalCount)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">человек</p>
        </div>
        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center col-span-2 sm:col-span-1">
          <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Ср. чек</p>
          <p className="text-lg font-bold text-blue-700 mt-1">{formatCompact(totalAvg)}</p>
          <p className="text-[11px] text-gray-400 mt-0.5">{formatNum(Math.round(totalAvg))} сом</p>
        </div>
      </div>

      {/* ── Segment Breakdown Table ── */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
          <h4 className="text-sm font-semibold text-gray-700">Детализация по сегментам клиентов</h4>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs text-left">
            <thead className="bg-gray-50 border-b text-gray-500 uppercase font-medium">
              <tr>
                <th className="px-4 py-2.5">Группа</th>
                <th className="px-4 py-2.5 text-right">Продажи</th>
                <th className="px-4 py-2.5 text-right">Уник. клиенты</th>
                <th className="px-4 py-2.5 text-right">Выручка (сом)</th>
                <th className="px-4 py-2.5 text-right">Прибыль (сом)</th>
                <th className="px-4 py-2.5 text-right">Ср. чек</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-mono">
              {CLIENT_GROUPS.map((group) => {
                const grpData = consultantData[group.key] || {};
                const sales   = grpData.sales_count || 0;
                const count   = grpData.count || 0;
                const sum     = grpData.sum || 0;
                const profit  = grpData.profit || 0;
                const avg     = grpData.avg_cheque || 0;

                const salesShare  = totalSales > 0 ? ((sales / totalSales) * 100).toFixed(1) : '0.0';
                const countShare  = totalCount > 0 ? ((count / totalCount) * 100).toFixed(1) : '0.0';
                const sumShare    = totalSum > 0 ? ((sum / totalSum) * 100).toFixed(1) : '0.0';
                const profitShare = totalProfit > 0 ? ((profit / totalProfit) * 100).toFixed(1) : '0.0';

                const isAll = group.key === 'all_clients';

                return (
                  <tr key={group.key} className={isAll ? 'bg-blue-50/40 font-semibold' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3 font-sans font-medium text-gray-900 whitespace-nowrap">
                      <span className={`inline-block w-2.5 h-2.5 rounded-full mr-2 ${group.color.badge}`} />
                      {group.labelFull}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>{formatNum(sales)}</div>
                      {!isAll && <div className="text-[10px] text-gray-400 font-sans">{salesShare}%</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>{formatNum(count)}</div>
                      {!isAll && <div className="text-[10px] text-gray-400 font-sans">{countShare}%</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>{formatCompact(sum)}</div>
                      {!isAll && <div className="text-[10px] text-gray-400 font-sans">{sumShare}%</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div>{formatCompact(profit)}</div>
                      {!isAll && <div className="text-[10px] text-gray-400 font-sans">{profitShare}%</div>}
                    </td>
                    <td className="px-4 py-3 text-right font-sans">
                      <div>{formatCompact(avg)}</div>
                      {!isAll && (
                        <div className="mt-0.5">
                          <SecondaryCell metric="avg_cheque" value={avg} allValue={totalAvg} colorClass={group.color.badge} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Revenue Contribution Visual Progress Bars ── */}
      <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm space-y-3">
        <h4 className="text-sm font-semibold text-gray-700">Распределение выручки по сегментам</h4>
        <div className="space-y-2.5">
          {CLIENT_GROUPS.filter((g) => g.key !== 'all_clients').map((group) => {
            const grpSum = (consultantData[group.key] || {}).sum || 0;
            const pct = totalSum > 0 ? Number(((grpSum / totalSum) * 100).toFixed(1)) : 0;
            return (
              <div key={group.key} className="space-y-1">
                <div className="flex justify-between text-xs font-medium text-gray-600">
                  <span className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${group.color.badge}`} />
                    {group.labelFull}
                  </span>
                  <span>{formatCompact(grpSum)} сом ({pct}%)</span>
                </div>
                <div className="w-full bg-gray-100 h-2 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      group.key === 'retail_clients' ? 'bg-gray-400'
                      : group.key === 'added_clients' ? 'bg-violet-500'
                      : group.key === 'app_clients' ? 'bg-orange-500'
                      : 'bg-green-500'
                    }`}
                    style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const ClientsByConsultantChart = ({ data }) => {
  // ── Layout context
  const { openModal } = useLayout();

  // ── UI state
  const [metric,          setMetric]          = useState('sales_count');
  const [showEmpty,       setShowEmpty]       = useState(false);
  const [showDefinitions, setShowDefinitions] = useState(false);
  const [sortByMetric,    setSortByMetric]    = useState(false); // false → order by consultant.id

  const toggleDefinitions = useCallback(() => setShowDefinitions((v) => !v), []);
  const toggleSort        = useCallback(() => setSortByMetric((v) => !v),    []);
  const toggleShowEmpty   = useCallback(() => setShowEmpty((v) => !v),       []);

  const handleConsultantClick = useCallback((row) => {
    if (openModal) {
      const consultantMeta = row.consultant?.meta || row.consultant || {};
      openModal({
        title: `Статистика консультанта: ${consultantMeta.name}`,
        component: <ConsultantSegmentStatsModal consultantData={row} />,
      });
    }
  }, [openModal]);

  // ── Derived
  const fmt         = FORMATTERS[metric] || formatNum;
  const metricLabel = METRIC_OPTIONS.find((m) => m.key === metric)?.label ?? '';

  const visibleGroups =
    metric === 'count'
      ? CLIENT_GROUPS.filter((g) => !COUNT_HIDDEN_GROUPS.has(g.key))
      : CLIENT_GROUPS;

  // ── Memoized data
  const processedData = useMemo(() => {
    const rows = Array.isArray(data) ? data : (data?.results || []);

    // 1. Filter consultants with zero sales (unless showEmpty)
    const filtered = showEmpty
      ? rows
      : rows.filter((item) => (item.all_clients?.sales_count || 0) > 0);

    // 2. Base order: always by consultant.id; optionally re-sort by chosen metric (desc)
    const byId      = [...filtered].sort((a, b) => ((a.consultant?.meta?.id ?? a.consultant?.id) || 0) - ((b.consultant?.meta?.id ?? b.consultant?.id) || 0));
    const tableData = sortByMetric
      ? [...byId].sort(
          (a, b) =>
            ((b.all_clients || {})[metric] || 0) -
            ((a.all_clients || {})[metric] || 0)
        )
      : byId;

    // 3. Grand totals: backend-provided when available (rows without sales add nothing)
    const grandTotals = data?.grand_totals || buildGrandTotals(filtered);

    // 4. Chart data follows table order so bar chart matches table visually
    const chartData = buildChartData(tableData, metric);

    // 5. Pie: share of all_clients[metric] per consultant
    const pieData = tableData.map((item) => ({
      name:  item.consultant?.meta?.name || item.consultant?.name,
      value: (item.all_clients || {})[metric] || 0,
    }));

    return { chartData, pieData, tableData, grandTotals };
  }, [data, metric, showEmpty, sortByMetric]);

  // ── Bar chart segment config (depends on metric)
  const barConfig = useMemo(() => {
    const groups =
      metric === 'count'
        ? CLIENT_GROUPS.filter((g) => !COUNT_HIDDEN_GROUPS.has(g.key))
        : CLIENT_GROUPS;
    return {
      dataKeys: groups.map((g) => g.labelFull),
      labels:   groups.map((g) => g.labelFull),
      colors:   groups.map((g) => BAR_COLORS[g.key]),
    };
  }, [metric]);

  const pieConfig = {
    dataKeys: ['value'],
    colors:   ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'],
  };

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="p-6 bg-gray-50 min-h-screen space-y-8">

      {/* ── Header & Controls ──────────────────────────────────────────────── */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-xl shadow-sm">
        <h2 className="text-xl font-bold text-gray-800">Статистика по консультантам</h2>
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={showEmpty}
            onChange={toggleShowEmpty}
            className="form-checkbox h-4 w-4 text-blue-600 rounded"
          />
          Показать пустые
        </label>
      </div>

      {/* ── Client Group Definitions ───────────────────────────────────────── */}
      <DefinitionsPanel open={showDefinitions} onToggle={toggleDefinitions} />

      {/* ── Summary Cards ─────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        {visibleGroups.map((group) => {
          const totals    = processedData.grandTotals[group.key]     || {};
          const allTotals = processedData.grandTotals['all_clients'] || {};
          return (
            <SummaryCard
              key={group.key}
              group={group}
              metric={metric}
              value={totals[metric]    || 0}
              allValue={allTotals[metric] || 0}
            />
          );
        })}
      </div>

      {/* ── Comparison Table ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
        <div className="px-6 py-4 border-b border-gray-100 space-y-3">
          <h3 className="text-lg font-semibold text-gray-700">Сравнение по консультантам</h3>
          <MetricTabBar
            metric={metric}
            onMetricChange={setMetric}
            sortEnabled={sortByMetric}
            onSortToggle={toggleSort}
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 border-b">
              <tr>
                <th className="px-5 py-3 font-medium" rowSpan={2}>Консультант</th>
                {visibleGroups.map((group) => (
                  <th
                    key={group.key}
                    className={`px-5 py-3 text-center ${group.color.header}`}
                    colSpan={group.key !== 'all_clients' ? 2 : 1}
                  >
                    {group.labelFull}
                  </th>
                ))}
              </tr>
              <tr className="text-xs text-gray-400 uppercase bg-gray-50 border-b">
                {/* all_clients — single value column */}
                <th className={`px-5 py-2 text-center ${CLIENT_GROUPS[0].color.header}`}>
                  {metricLabel}
                </th>
                {/* remaining groups — value + secondary column */}
                {visibleGroups.slice(1).map((group) => (
                  <React.Fragment key={group.key}>
                    <th className={`px-5 py-2 text-center ${group.color.header}`}>{metricLabel}</th>
                    <th className={`px-5 py-2 text-center ${group.color.header}`}>
                      {metric === 'avg_cheque' ? '± vs Все' : '% от всех'}
                    </th>
                  </React.Fragment>
                ))}
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {processedData.tableData.map((row) => {
                const consultantMeta = row.consultant?.meta || row.consultant || {};
                const allVal = (row.all_clients || {})[metric] || 0;
                return (
                  <tr key={consultantMeta.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-4 font-medium text-gray-900 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => handleConsultantClick(row)}
                        className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-800 hover:underline font-semibold text-left focus:outline-none group cursor-pointer"
                        title="Нажмите, чтобы открыть статистику консультанта"
                      >
                        <span>{consultantMeta.name}</span>
                        <svg className="w-3.5 h-3.5 text-blue-400 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </button>
                    </td>
                    {visibleGroups.map((group, idx) => {
                      const val = (row[group.key] || {})[metric] || 0;
                      return (
                        <React.Fragment key={group.key}>
                          <td className={`px-5 py-4 text-center font-mono ${group.color.cell}`}>
                            {fmt(val)}
                          </td>
                          {idx > 0 && (
                            <td className={`px-5 py-4 text-center ${group.color.cell}`}>
                              <SecondaryCell
                                metric={metric}
                                value={val}
                                allValue={allVal}
                                colorClass={group.color.badge}
                              />
                            </td>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>

            <tfoot className="bg-gray-100 border-t-2 border-gray-300">
              <tr className="font-bold">
                <td className="px-5 py-4 text-gray-900">ИТОГО</td>
                {visibleGroups.map((group, idx) => {
                  const totals   = processedData.grandTotals[group.key]      || {};
                  const allTotal = (processedData.grandTotals['all_clients'] || {})[metric] || 0;
                  const val      = totals[metric] || 0;
                  return (
                    <React.Fragment key={group.key}>
                      <td className={`px-5 py-4 text-center ${group.color.foot}`}>
                        {fmt(val)}
                      </td>
                      {idx > 0 && (
                        <td className={`px-5 py-4 text-center ${group.color.foot}`}>
                          <SecondaryCell
                            metric={metric}
                            value={val}
                            allValue={allTotal}
                            colorClass={group.color.badge}
                            bold
                          />
                        </td>
                      )}
                    </React.Fragment>
                  );
                })}
              </tr>
            </tfoot>
          </table>
        </div>

        {processedData.tableData.length === 0 && (
          <div className="p-8 text-center text-gray-500">Нет данных для отображения</div>
        )}
      </div>

      {/* ── Charts Section ─────────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-3">
          <span className="text-2xl">📊</span>
          <h3 className="text-lg font-semibold text-gray-700">
            Графики — {metricLabel}
          </h3>
          <span className="ml-auto text-xs text-gray-400 italic">
            Метрика синхронизирована с таблицей
          </span>
        </div>

        <div className="p-6 space-y-6">
          {/* Bar chart — full width */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <h4 className="text-base font-semibold text-gray-700 mb-4">
              Сравнение групп клиентов по консультантам
            </h4>
            <div className="h-[400px] w-full">
              <CustomStackedBarChart data={processedData.chartData} config={barConfig} />
            </div>
          </div>

          {/* Pie chart — full width */}
          <div className="bg-gray-50 p-4 rounded-xl">
            <h4 className="text-base font-semibold text-gray-700 mb-4">
              Доля по консультантам — все клиенты
            </h4>
            <div className="h-[400px] w-full">
              <CustomPieChart data={processedData.pieData} config={pieConfig} />
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ClientsByConsultantChart;